/**
 * ThemeContext - Contexte pour les thèmes
 *
 * Utilise PreferencesContext comme source de vérité pour :
 * - selectedThemeId (ID du thème sélectionné)
 *
 * Ce contexte gère la logique d'application des thèmes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    getThemes,
    getThemeById,
    applyTheme,
    getStoredTheme
} from '../services/themesService';
import { usePreferences } from './PreferencesContext';

const ThemeContext = createContext();

// Default theme (Cabaza Purple) - fallback when no theme is loaded
const DEFAULT_THEME = {
    id: 1,
    name: 'Cabaza Purple',
    nameFr: 'Violet Cabaza',
    nameEn: 'Cabaza Purple',
    themeType: 'gradient',
    primaryColor: '#8b5cf6',
    secondaryColor: '#ec4899',
    accentColor: '#8b5cf6',
    backgroundColor: '#ffffff',
    surfaceColor: '#f9fafb',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textOnPrimary: '#ffffff',
    borderColor: '#e5e7eb',
    isSystem: true,
    isDefault: true
};

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState(() => {
        // Try to get stored theme first
        const stored = getStoredTheme();
        return stored || DEFAULT_THEME;
    });
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [themeReady, setThemeReady] = useState(true);

    // Get selectedThemeId from centralized PreferencesContext
    const { selectedThemeId: prefSelectedThemeId, updateSelectedThemeId, isLoaded: preferencesLoaded } = usePreferences();
    const selectedThemeId = prefSelectedThemeId ?? 0;

    // Load all themes on mount
    useEffect(() => {
        const loadThemes = async () => {
            try {
                const allThemes = await getThemes();
                setThemes(allThemes);
            } catch (error) {
                console.error('Error loading themes:', error);
            } finally {
                setLoading(false);
            }
        };
        loadThemes();
    }, []);

    // Apply theme when selectedThemeId changes (from PreferencesContext)
    useEffect(() => {
        const applySelectedTheme = async () => {
            if (!preferencesLoaded || themes.length === 0) return;

            if (selectedThemeId && selectedThemeId > 0) {
                // Find theme in loaded themes or fetch it
                let theme = themes.find(t => t.id === selectedThemeId);
                if (!theme) {
                    theme = await getThemeById(selectedThemeId);
                }
                if (theme) {
                    console.log('[Theme] Applying theme from preferences:', theme.name);
                    setCurrentTheme(theme);
                    applyTheme(theme);
                    return;
                }
            }

            // No valid theme ID - use localStorage or default
            const stored = getStoredTheme();
            if (stored && stored.id) {
                setCurrentTheme(stored);
                applyTheme(stored);
            } else {
                const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME;
                setCurrentTheme(defaultTheme);
                applyTheme(defaultTheme);
            }
        };

        applySelectedTheme();
    }, [selectedThemeId, preferencesLoaded, themes]);

    // Listen for login event to show loading state
    useEffect(() => {
        const handleLogin = () => {
            console.log('[Theme] User logged in - waiting for preferences');
            setThemeReady(false);
            // Theme will be applied when PreferencesContext loads and selectedThemeId changes
            setTimeout(() => setThemeReady(true), 500);
        };

        window.addEventListener('userLoggedIn', handleLogin);
        return () => window.removeEventListener('userLoggedIn', handleLogin);
    }, []);

    // Listen for logout event to reset theme
    useEffect(() => {
        const handleLogout = () => {
            console.log('[Theme] User logged out - resetting to default theme');
            const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME;
            setCurrentTheme(defaultTheme);
            applyTheme(defaultTheme);
        };

        window.addEventListener('userLoggedOut', handleLogout);
        return () => window.removeEventListener('userLoggedOut', handleLogout);
    }, [themes]);

    // Change theme (saves to PreferencesContext which handles localStorage + DB)
    const changeTheme = useCallback((theme) => {
        setCurrentTheme(theme);
        applyTheme(theme); // This saves to localStorage for immediate availability

        // Save to PreferencesContext (which syncs to DB)
        if (theme.id) {
            updateSelectedThemeId(theme.id);
        }
    }, [updateSelectedThemeId]);

    // Apply custom theme (from color picker)
    const applyCustomTheme = useCallback((customColors) => {
        const customTheme = {
            ...DEFAULT_THEME,
            ...customColors,
            id: 0,
            name: 'Custom',
            nameFr: 'Personnalisé',
            nameEn: 'Custom',
            themeType: 'custom',
            isSystem: false,
            isDefault: false
        };

        setCurrentTheme(customTheme);
        applyTheme(customTheme);
    }, []);

    // Reset to default theme
    const resetToDefault = useCallback(() => {
        const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME;
        changeTheme(defaultTheme);
    }, [themes, changeTheme]);

    // Refresh themes list
    const refreshThemes = useCallback(async () => {
        const allThemes = await getThemes();
        setThemes(allThemes);
    }, []);

    // Check if current theme is dark
    const isDarkTheme = currentTheme?.themeType === 'flat-dark';

    const value = {
        currentTheme,
        themes,
        loading,
        themeReady,
        isDarkTheme,
        changeTheme,
        applyCustomTheme,
        resetToDefault,
        refreshThemes,
        DEFAULT_THEME
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
